<template>
  <div :class="{ 'playing': isPlaying }" :data-video-watched="videoWatched" class="questionnaire-question-content" @mousemove="onContainerMouseMove" @touchstart.passive="onContainerMouseMove">
    <div v-show="videoIsLoaded" class="video-container">
      <div class="video-wrapper" @mouseenter="onWrapperMouseEnter" @mouseleave="onWrapperMouseLeave" @mousemove="onWrapperMouseMove">
        <video
            ref="videoPlayer"
            class="video-player"
            controlsList="nodownload noplaybackrate nofullscreen"
            disablePictureInPicture
            disableRemotePlayback
            preload="metadata"
            @canplay="onCanPlay"
            @ended="onVideoEnded"
            @error="onVideoError"
            @loadedmetadata="onVideoLoaded"
            @loadstart="onLoadStart"
            @pause="onVideoPause"
            @play="onVideoPlay"
            @timeupdate="onTimeUpdate"
            @click.stop.prevent="togglePlay"
            @contextmenu.prevent
        >
          <source v-if="hasMp4()" :src="mp4Src" type="video/mp4">
          <source v-if="hasWebm()" :src="webmSrc" type="video/webm">
          Votre navigateur ne supporte pas la lecture de vidéos.
        </video>
        <div :class="{ 'show': showOverlay }" class="video-overlay" @click="togglePlay">
          <button :disabled="isLoadingNext" class="back-later-button-overlay" @click.stop="handleBackLater">
            <span v-if="isLoadingNext" class="spinner-button"></span>
            <span v-else>{{ videoWatched ? 'Passer aux tests' : 'Regarder plus tard' }}</span>
          </button>
          <div class="play-pause-icon">
            <span v-if="!isPlaying" class="play-icon">▶</span>
            <span v-else class="pause-icon">⏸</span>
          </div>
        </div>
        <div :class="{ 'hide': !showControls }" class="video-custom-controls">
          <div class="controls-main">
            <button class="control-button" @click="togglePlayFromControl">
              <span v-if="!isPlaying">▶</span>
              <span v-else class="control-pause-icon">⏸</span>
            </button>

            <div class="progress-container">
              <div class="progress-bar">
                <div :style="{ width: progressPercent + '%' }" class="progress-fill"></div>
              </div>
              <span class="time-display">{{ currentTimeDisplay }} / {{ durationDisplay }}</span>
            </div>

          </div>
        </div>
      </div>

    </div>
    <div v-if="videoWatched" class="video-completion-notification">
      ✓ Vidéo visionnée dans son intégralité
    </div>
    <div v-if="videoError" class="video-error-notification">
      <div class="error-icon">⚠️</div>
      <div class="error-message">
        <strong>Erreur de chargement de la vidéo</strong>
        <p>{{ videoErrorMessage }}</p>
        <button class="retry-button" @click="retryVideo">Réessayer</button>
        <button :disabled="isLoadingNext" class="skip-button" @click="validateVideoCompletion(true)">
          <span v-if="isLoadingNext" class="spinner-button"></span>
          <span v-else>Passer aux tests</span>
        </button>
      </div>
    </div>
    <span v-show="!videoIsLoaded && !videoError" class="spinner-border"/>
  </div>
</template>

<script setup>
import {computed, onMounted, onUnmounted, ref} from 'vue'
import UserService from "@/services/user.service";

const emit = defineEmits(['video-completed', 'back-later', 'video-near-end'])
const props = defineProps({
  question: String,
  userId: Number
})
const videoPlayer = ref(null)
const isPlaying = ref(false)
const currentTime = ref(0)
const duration = ref(0)
const videoWatched = ref(false)
const maxTimeReached = ref(0)
const videoIsLoaded = ref(false)
const showOverlay = ref(false)
const showControls = ref(true)
const nearEndEmitted = ref(false)
const videoError = ref(false)
const videoErrorMessage = ref('')
const loadAttempts = ref(0)
const maxLoadAttempts = 3
const isLoadingNext = ref(false)
let hideOverlayTimeout = null
let showOverlayTimeout = null
let hideControlsTimeout = null

onMounted(() => {
  const video = videoPlayer.value
  if (!video) return

  video.controls = false
  video.addEventListener('seeking', onSeeking)
  video.addEventListener('seeked', onSeeked)
  video.addEventListener('mousedown', onMouseDown, {capture: true})
  video.addEventListener('mousemove', onMouseMove, {capture: true})
  video.addEventListener('dblclick', onDblClick, {capture: true})
  document.addEventListener('keydown', handleKeyDown)
})

onUnmounted(() => {
  const video = videoPlayer.value

  if (video) {
    video.removeEventListener('seeking', onSeeking)
    video.removeEventListener('seeked', onSeeked)
    video.removeEventListener('mousedown', onMouseDown, true)
    video.removeEventListener('mousemove', onMouseMove, true)
    video.removeEventListener('dblclick', onDblClick, true)
  }

  document.removeEventListener('keydown', handleKeyDown)

  clearTimeout(hideOverlayTimeout)
  clearTimeout(showOverlayTimeout)
  clearTimeout(hideControlsTimeout)
})

const webmSrc = computed(() => {
  if (!props.question) return ''
  const filename = props.question.replace(/\.[^/.]+$/, '.webm')
  return require('@/assets/video/' + filename)
})

const mp4Src = computed(() => {
  if (!props.question) return ''
  const filename = props.question.replace(/\.[^/.]+$/, '.mp4')
  return require('@/assets/video/' + filename)
})

const progressPercent = computed(() => {
  if (duration.value === 0) return 0
  return (maxTimeReached.value / duration.value) * 100
})

const currentTimeDisplay = computed(() => formatTime(currentTime.value))
const durationDisplay = computed(() => formatTime(duration.value))
const hasWebm = () => {
  try {
    if (!props.question) return false
    const filename = props.question.replace(/\.[^/.]+$/, '.webm')
    require('@/assets/video/' + filename)
    return true
  } catch (e) {
    console.error('Failed to load WebM video:', e)
    return false
  }
}

const hasMp4 = () => {
  try {
    if (!props.question) return false
    const filename = props.question.replace(/\.[^/.]+$/, '.mp4')
    require('@/assets/video/' + filename)
    return true
  } catch (e) {
    console.error('Failed to load MP4 video:', e)
    return false
  }
}
const onVideoLoaded = () => {
  duration.value = videoPlayer.value.duration
  videoIsLoaded.value = true
  videoError.value = false
  loadAttempts.value = 0
  console.log('[Video] Metadata loaded successfully', {
    duration: duration.value,
    webmSrc: hasWebm() ? webmSrc.value : 'N/A',
    mp4Src: hasMp4() ? mp4Src.value : 'N/A',
    question: props.question
  })
}

const onLoadStart = () => {
  console.log('[Video] Loading started', {
    webmSrc: hasWebm() ? webmSrc.value : 'N/A',
    mp4Src: hasMp4() ? mp4Src.value : 'N/A',
    attempt: loadAttempts.value + 1
  })
}

const onCanPlay = () => {
  console.info('[Video] Can play - video is ready')
}

const onVideoError = () => {
  loadAttempts.value++
  const error = videoPlayer.value?.error

  let errorMsg = 'Une erreur est survenue lors du chargement de la vidéo.'
  let detailedError = 'Unknown error'

  if (error) {
    switch (error.code) {
      case 1:
        detailedError = 'MEDIA_ERR_ABORTED'
        errorMsg = 'Le chargement de la vidéo a été interrompu.'
        break
      case 2:
        detailedError = 'MEDIA_ERR_NETWORK'
        errorMsg = 'Une erreur réseau est survenue. Vérifiez votre connexion internet.'
        break
      case 3:
        detailedError = 'MEDIA_ERR_DECODE'
        errorMsg = 'La vidéo ne peut pas être lue (erreur de décodage).'
        break
      case 4:
        detailedError = 'MEDIA_ERR_SRC_NOT_SUPPORTED'
        errorMsg = 'Le format de la vidéo n\'est pas supporté par votre navigateur.'
        break
      default:
        detailedError = `Error code: ${error.code}`
    }
  }

  console.error('[Video] Load error', {
    errorCode: error?.code,
    errorMessage: error?.message,
    detailedError,
    webmSrc: hasWebm() ? webmSrc.value : 'N/A',
    mp4Src: hasMp4() ? mp4Src.value : 'N/A',
    question: props.question,
    attempt: loadAttempts.value,
    userAgent: navigator.userAgent
  })

  if (loadAttempts.value < maxLoadAttempts) {
    console.log(`[Video] Retrying... (${loadAttempts.value}/${maxLoadAttempts})`)
    setTimeout(() => {
      videoPlayer.value?.load()
    }, 1000)
  } else {
    videoError.value = true
    videoErrorMessage.value = errorMsg
    videoIsLoaded.value = false
  }
}

const retryVideo = () => {
  console.log('[Video] Manual retry triggered')
  videoError.value = false
  videoErrorMessage.value = ''
  loadAttempts.value = 0
  videoPlayer.value?.load()
}

const onTimeUpdate = () => {
  if (null === videoPlayer.value) {
    return;
  }
  currentTime.value = videoPlayer.value.currentTime

  // Track the maximum time reached (user cannot skip forward)
  if (currentTime.value > maxTimeReached.value) {
    maxTimeReached.value = currentTime.value
  }

  // If user tries to skip forward, reset to maxTimeReached
  if (currentTime.value > maxTimeReached.value + 0.5) {
    videoPlayer.value.currentTime = maxTimeReached.value
  }

  // Emit event 13 seconds before the end
  if (!nearEndEmitted.value && duration.value > 0 && currentTime.value >= duration.value - 13) {
    validateVideoCompletion(false)
  }
}
const onSeeking = (e) => {
  if (!videoPlayer.value) return
  if (videoPlayer.value.currentTime > maxTimeReached.value) {
    e.preventDefault()
    e.stopImmediatePropagation()
    videoPlayer.value.currentTime = maxTimeReached.value
  }
}

const onSeeked = () => {
  if (!videoPlayer.value) return
  if (videoPlayer.value.currentTime > maxTimeReached.value) {
    videoPlayer.value.currentTime = maxTimeReached.value
  }
}


const validateVideoCompletion = (emitFinish) => {
  nearEndEmitted.value = true
  if (emitFinish) {
    isLoadingNext.value = true
  }
  UserService.terminatedTest(props.userId).then(() => {
    if (emitFinish) {
      return
    }
    videoWatched.value = true
  });
  if (emitFinish) {
    emit('video-completed')
  } else {
    emit('video-near-end')
  }
}

const handleBackLater = () => {
  isLoadingNext.value = true
  emit('back-later')
}

const onVideoEnded = () => {
  isPlaying.value = false
  emit('video-completed')
}

const onVideoPlay = () => {
  isPlaying.value = true
}

const onVideoPause = () => {
  isPlaying.value = false
}

const togglePlay = (showOverlayFeedback = true) => {
  const video = videoPlayer.value
  if (null === video) {
    return;
  }
  if (video.paused) {
    video.play().catch(err => {
      console.error('[Video] Play failed', err)
      // Don't show error UI for play() failures, as they're often due to user interaction policies
    })
  } else {
    video.pause()
  }

  // Show overlay briefly then hide it only if requested (video click)
  if (showOverlayFeedback) {
    showOverlay.value = true
    if (hideOverlayTimeout) {
      clearTimeout(hideOverlayTimeout)
    }
    hideOverlayTimeout = setTimeout(() => {
      showOverlay.value = false
    }, 500)
  }
}

const togglePlayFromControl = () => {
  togglePlay(false)
}

const formatTime = (seconds) => {
  if (isNaN(seconds)) return '0:00'
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

const onWrapperMouseEnter = () => {
  if (hideOverlayTimeout) {
    clearTimeout(hideOverlayTimeout)
  }
  showOverlay.value = true
}

const onWrapperMouseLeave = () => {
  if (showOverlayTimeout) {
    clearTimeout(showOverlayTimeout)
  }
  if (hideOverlayTimeout) {
    clearTimeout(hideOverlayTimeout)
  }
  showOverlay.value = false
}

const onWrapperMouseMove = () => {
  if (showOverlayTimeout) {
    clearTimeout(showOverlayTimeout)
  }
  if (hideOverlayTimeout) {
    clearTimeout(hideOverlayTimeout)
  }

  if (!showOverlay.value) {
    showOverlayTimeout = setTimeout(() => {
      showOverlay.value = true
      hideOverlayTimeout = setTimeout(() => {
        showOverlay.value = false
      }, 2000)
    }, 500)
  } else {
    showOverlay.value = true
    hideOverlayTimeout = setTimeout(() => {
      showOverlay.value = false
    }, 2000)
  }
}

const onContainerMouseMove = () => {
  if (hideControlsTimeout) {
    clearTimeout(hideControlsTimeout)
  }
  showControls.value = true
  hideControlsTimeout = setTimeout(() => {
    showControls.value = false
  }, 2000)
}

// Handle keyboard shortcuts
const handleKeyDown = (e) => {
  // Block arrow keys (left/right could seek)
  if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
    e.preventDefault()
    e.stopPropagation()
  }

  // Allow spacebar for play/pause
  if (e.key === ' ' || e.key === 'Spacebar') {
    e.preventDefault()
    togglePlay()
  }
}
// Prevent seeking/scrubbing on the timeline but allow other interactions
const preventTimelineInteraction = (e) => {
  // Allow clicks on video itself for play/pause
  if (e.target === videoPlayer.value) {
    return
  }

  // Block interaction with native controls timeline
  e.preventDefault()
  e.stopPropagation()
  e.stopImmediatePropagation()
}
const onMouseMove = (e) => {
  // Only prevent if dragging (buttons pressed)
  if (e.buttons !== 0 && e.target === videoPlayer.value) {
    e.preventDefault()
    e.stopPropagation()
  }
}
const onMouseDown = (e) => {
  // Allow single clicks for play/pause
  if (e.target === videoPlayer.value && e.button === 0) {
    return
  }
  preventTimelineInteraction(e)
}
const onDblClick = (e) => {
  // Prevent double-click (could trigger fullscreen in some browsers)
  if (e.target === videoPlayer.value) {
    e.preventDefault()
    e.stopPropagation()
  }
}
</script>

<style scoped>
.questionnaire-question-content {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: transparent;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.5s ease;
}

.questionnaire-question-content.playing {
  background: rgba(0, 0, 0, 0.85);
}

.video-container {
  position: relative;
  max-width: 800px;
  width: 100%;
  padding: 1rem;
  max-height: 100vh;
  display: flex;
  flex-direction: column;
}

@media (max-width: 768px) {
  .video-container {
    padding: 0.5rem;
    max-width: 100%;
  }
}

.video-wrapper {
  position: relative;
  cursor: pointer;
  overflow: hidden;
}

.video-player {
  width: 100%;
  height: auto;
  max-height: calc(100vh - 150px);
  background: #000;
  border-radius: 8px;
  display: block;
  object-fit: contain;
  position: relative;
  z-index: 1;
}

.video-player::-webkit-media-controls {
  display: none !important;
}

.video-player::-webkit-media-controls-enclosure {
  display: none !important;
}

.video-player::-webkit-media-controls-panel {
  display: none !important;
}

.video-player::-webkit-media-controls-timeline {
  display: none !important;
}

.video-player::-webkit-media-controls-current-time-display,
.video-player::-webkit-media-controls-time-remaining-display {
  display: none !important;
}

@media (max-width: 768px) {
  .video-player {
    max-height: calc(100vh - 120px);
    border-radius: 4px 4px 0 0;
  }
}

.video-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
  pointer-events: none;
  z-index: 2;
}

.video-overlay.show {
  opacity: 1 !important;
  pointer-events: auto;
}

.video-player:fullscreen {
  cursor: pointer;
  width: 100vw;
  height: 100vh;
  object-fit: contain;
}

.video-player:fullscreen::backdrop {
  background: #000;
}

.video-player:fullscreen::-webkit-media-controls {
  display: none !important;
}

.video-player:fullscreen::-webkit-media-controls-enclosure {
  display: none !important;
}

.video-player:fullscreen::-webkit-media-controls-panel {
  display: none !important;
}

.video-player:-webkit-full-screen {
  cursor: pointer;
  width: 100vw;
  height: 100vh;
  object-fit: contain;
}

.video-player:-webkit-full-screen::-webkit-media-controls {
  display: none !important;
}

.video-player:-webkit-full-screen::-webkit-media-controls-enclosure {
  display: none !important;
}

.video-player:-moz-full-screen {
  cursor: pointer;
  width: 100vw;
  height: 100vh;
  object-fit: contain;
}

.video-player:-moz-full-screen::backdrop {
  background: #000;
}

.video-player:-ms-fullscreen {
  cursor: pointer;
  width: 100vw;
  height: 100vh;
  object-fit: contain;
}

/* Force hide all native controls in all states */
video::-internal-media-controls-overlay-cast-button {
  display: none !important;
}

video::-webkit-media-controls-play-button,
video::-webkit-media-controls-volume-slider,
video::-webkit-media-controls-mute-button,
video::-webkit-media-controls-timeline,
video::-webkit-media-controls-current-time-display,
video::-webkit-media-controls-time-remaining-display,
video::-webkit-media-controls-fullscreen-button {
  display: none !important;
}

/* Hide overlay when video is in fullscreen (it won't be visible anyway) */
.video-wrapper:has(.video-player:fullscreen) .video-overlay {
  display: none;
}

.back-later-button-overlay {
  position: absolute;
  top: 1rem;
  right: 1rem;
  padding: 0.75rem 1.5rem;
  background: rgba(0, 0, 0, 0.5);
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  cursor: pointer;
  transition: background 0.2s ease;
  pointer-events: auto;
  backdrop-filter: blur(5px);
}

.back-later-button-overlay:hover {
  background: rgba(0, 0, 0, 0.8);
}

.back-later-button-overlay:disabled {
  cursor: not-allowed;
  opacity: 0.7;
}

@media (max-width: 768px) {
  .back-later-button-overlay {
    top: 0.75rem;
    right: 0.75rem;
    padding: 0.6rem 1.2rem;
    font-size: 0.9rem;
  }
}

.play-pause-icon:hover {
  background: rgba(0, 0, 0, 0.8);
}

.play-pause-icon {
  background: rgba(0, 0, 0, 0.50);
  border-radius: 50%;
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.5rem;
  color: white;
}

@media (max-width: 768px) {
  .play-pause-icon {
    width: 60px;
    height: 60px;
    font-size: 1.8rem;
  }
}

.play-pause-icon span {
  display: flex;
  align-items: center;
  justify-content: center;
}

.play-icon {
  font-size: 2.5rem;
  margin-left: 4px;
  margin-bottom: 8px;
}

@media (max-width: 768px) {
  .play-icon {
    font-size: 1.8rem;
    margin-left: 3px;
    margin-bottom: 6px;
  }
}

.pause-icon {
  font-size: 3.5rem;
  margin-bottom: 4px;
}

@media (max-width: 768px) {
  .pause-icon {
    font-size: 2.5rem;
    margin-bottom: 3px;
  }
}

.control-pause-icon {
  font-size: 2.5rem;
  margin-bottom: 4px;
}

@media (max-width: 768px) {
  .control-pause-icon {
    font-size: 1.8rem;
    margin-bottom: 3px;
  }
}

.video-custom-controls {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 1rem;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.8), transparent);
  border-radius: 0;
  opacity: 1;
  transform: translateY(0);
  transition: opacity 0.3s ease, transform 0.3s ease;
  z-index: 3;
}

.video-custom-controls.hide {
  opacity: 0;
  transform: translateY(20px);
  pointer-events: none;
}

@media (max-width: 768px) {
  .video-custom-controls {
    padding: 0.75rem;
    border-radius: 0 0 4px 4px;
  }
}

.controls-main {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

@media (max-width: 768px) {
  .controls-main {
    gap: 0.5rem;
  }
}

.control-button {
  padding: 0.5rem;
  font-size: 1.2rem;
  background: transparent;
  color: white;
  border: none;
  cursor: pointer;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

@media (max-width: 768px) {
  .control-button {
    padding: 0.35rem;
    font-size: 1rem;
    width: 35px;
    height: 35px;
  }
}

.control-button:hover {
  background: rgba(255, 255, 255, 0.1);
}

.progress-container {
  flex: 1;
  min-width: 0;
}

.progress-bar {
  width: 100%;
  height: 6px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 3px;
  overflow: hidden;
  margin-bottom: 0.5rem;
}

@media (max-width: 768px) {
  .progress-bar {
    height: 5px;
    margin-bottom: 0.35rem;
  }
}

.progress-fill {
  height: 100%;
  background: #fff;
  transition: width 0.3s ease;
}

.time-display {
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.8);
}

@media (max-width: 768px) {
  .time-display {
    font-size: 0.75rem;
  }
}

.video-completion-notification {
  position: fixed;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  padding: 0.75rem 1.5rem;
  background: rgba(21, 87, 36, 0.95);
  color: white;
  border-radius: 8px;
  text-align: center;
  font-weight: 500;
  font-size: 0.95rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(10px);
  z-index: 10000;
  animation: slideUp 0.4s ease-out;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}

@media (max-width: 768px) {
  .video-completion-notification {
    bottom: 1.5rem;
    padding: 0.6rem 1.2rem;
    font-size: 0.85rem;
  }
}

.spinner-border {
  position: absolute;
  top: 50%;
  left: 50%;
  height: 50px;
  width: 50px;
  color: #0E6076;
}

.spinner-button {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.video-error-notification {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  max-width: 440px;
  width: 90%;
  padding: 2rem 1.75rem;
  background: white;
  color: #1f2937;
  border-radius: 16px;
  text-align: center;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.15);
  z-index: 10001;
  animation: slideIn 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}

.error-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
  opacity: 0.9;
}

.error-message strong {
  display: block;
  font-size: 1.15rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: #111827;
}

.error-message p {
  margin: 0 0 1.75rem;
  font-size: 0.95rem;
  color: #6b7280;
  line-height: 1.5;
}

.retry-button, .skip-button {
  padding: 0.75rem 1.5rem;
  margin: 0.35rem;
  border: none;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 120px;
}

.retry-button {
  background: #0E6076;
  color: white;
}

.retry-button:hover {
  background: #0a4d5f;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(14, 96, 118, 0.3);
}

.skip-button {
  background: #f3f4f6;
  color: #4b5563;
}

.skip-button:hover {
  background: #e5e7eb;
  transform: translateY(-1px);
}

.skip-button:disabled {
  cursor: not-allowed;
  opacity: 0.7;
}

.skip-button .spinner-button {
  border-color: rgba(75, 85, 99, 0.3);
  border-top-color: #4b5563;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translate(-50%, -50%) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
}

@media (max-width: 768px) {
  .video-error-notification {
    padding: 1.75rem 1.5rem;
    width: 92%;
    max-width: 380px;
  }

  .error-icon {
    font-size: 2.5rem;
  }

  .error-message strong {
    font-size: 1.05rem;
  }

  .error-message p {
    font-size: 0.9rem;
    margin-bottom: 1.5rem;
  }

  .retry-button, .skip-button {
    padding: 0.7rem 1.25rem;
    font-size: 0.9rem;
    min-width: 110px;
  }
}
</style>
